import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal, ScrollMaskLine } from "./Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, ArrowRight, Paperclip, X, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Logo + Brand Mark",
  "Digital Marketing",
  "Print + Displays",
  "Email + Publications",
  "Custom Creative",
];
const BUDGETS = ["< $1k", "$1k – $5k", "$5k – $10k", "$10k+"];

const inputClass =
  "bg-[#120B24] border-[#2D1B4E] text-white placeholder:text-[#6d5b95] focus-visible:ring-[#8A2BE2] focus-visible:border-[#8A2BE2] h-12 rounded-xl";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    project_type: "",
    budget: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds the 10MB limit.`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await axios.post(`${API}/upload`, fd);
        setAttachments((prev) => [...prev, data]);
      } catch (err) {
        const msg =
          err?.response?.data?.detail || "Upload failed. Please try again.";
        toast.error(`${file.name}: ${msg}`);
      }
    }
    setUploading(false);
  };

  const removeAttachment = (path) =>
    setAttachments((prev) => prev.filter((a) => a.path !== path));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    if (!emailValid(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, attachments });
      toast.success("Message sent! We'll be in touch soon.");
      setForm({
        name: "",
        email: "",
        project_type: "",
        budget: "",
        message: "",
      });
      setAttachments([]);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-40 bg-[#090514] overflow-hidden"
    >
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 h-[70vh] w-[70vh] glow-radial opacity-50" />
      <div className="relative mx-auto max-w-[1500px] px-6 md:px-12 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.35em] text-[#C77DFF] mb-6">
            Start a Project
          </div>
          <h2 className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[0.92]">
            <ScrollMaskLine>Let&apos;s build</ScrollMaskLine>
            <ScrollMaskLine delay={0.1}>something</ScrollMaskLine>
            <ScrollMaskLine delay={0.2} className="text-[#C77DFF]">
              unmistakable.
            </ScrollMaskLine>
          </h2>
          <Reveal delay={0.3} className="mt-8 text-[#B5A6CC] max-w-sm leading-relaxed">
            Tell us about your project. We&apos;ll get back within one business day
            with next steps.
          </Reveal>
          <Reveal delay={0.4} className="mt-10">
            <a
              href="mailto:hello@cmddesignlab.com"
              data-testid="contact-email-link"
              className="font-display text-xl md:text-2xl text-white link-underline"
            >
              hello@cmddesignlab.com
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <motion.form
            data-testid="contact-form"
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="rounded-3xl border border-[#2D1B4E] bg-[#0b0618]/60 backdrop-blur-xl p-6 md:p-10 space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#B5A6CC]">
                  Name
                </Label>
                <Input
                  id="name"
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Jane Doe"
                  className={inputClass}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#B5A6CC]">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="jane@company.com"
                  className={inputClass}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#B5A6CC]">Project Type</Label>
                <Select
                  value={form.project_type}
                  onValueChange={(v) => update("project_type", v)}
                >
                  <SelectTrigger
                    data-testid="contact-projecttype-trigger"
                    className={inputClass}
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#120B24] border-[#2D1B4E] text-white">
                    {PROJECT_TYPES.map((p) => (
                      <SelectItem
                        key={p}
                        value={p}
                        className="focus:bg-[#8A2BE2] focus:text-white"
                      >
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#B5A6CC]">Budget</Label>
                <Select
                  value={form.budget}
                  onValueChange={(v) => update("budget", v)}
                >
                  <SelectTrigger
                    data-testid="contact-budget-trigger"
                    className={inputClass}
                  >
                    <SelectValue placeholder="Select a range" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#120B24] border-[#2D1B4E] text-white">
                    {BUDGETS.map((b) => (
                      <SelectItem
                        key={b}
                        value={b}
                        className="focus:bg-[#8A2BE2] focus:text-white"
                      >
                        {b}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-[#B5A6CC]">
                Tell us about your project
              </Label>
              <Textarea
                id="message"
                data-testid="contact-message-input"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="What are you building, and what does success look like?"
                className="bg-[#120B24] border-[#2D1B4E] text-white placeholder:text-[#6d5b95] focus-visible:ring-[#8A2BE2] focus-visible:border-[#8A2BE2] rounded-xl min-h-[140px]"
              />
            </div>

            {/* Attachments */}
            <div className="space-y-3">
              <Label className="text-[#B5A6CC]">
                Attach a brief or references{" "}
                <span className="text-[#6d5b95]">
                  (images or PDF, up to 10MB)
                </span>
              </Label>
              <label
                htmlFor="contact-file-input"
                data-testid="contact-attach-label"
                className="flex items-center gap-3 cursor-pointer rounded-xl border border-dashed border-[#3a2166] hover:border-[#8A2BE2] bg-[#120B24] px-4 py-4 transition-colors duration-300"
              >
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#8A2BE2]/15 text-[#C77DFF]">
                  {uploading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Paperclip className="h-5 w-5" />
                  )}
                </span>
                <span className="text-sm text-[#B5A6CC]">
                  {uploading
                    ? "Uploading..."
                    : "Click to upload files"}
                </span>
                <input
                  id="contact-file-input"
                  data-testid="contact-file-input"
                  type="file"
                  multiple
                  accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
                  className="hidden"
                  onChange={(e) => handleFiles(e.target.files)}
                />
              </label>

              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="attachment-list">
                  {attachments.map((a) => (
                    <div
                      key={a.path}
                      data-testid="attachment-chip"
                      className="flex items-center gap-2 rounded-full border border-[#2D1B4E] bg-[#0b0618] pl-3 pr-2 py-1.5"
                    >
                      <FileText className="h-3.5 w-3.5 text-[#C77DFF]" />
                      <span className="text-xs text-white max-w-[160px] truncate">
                        {a.filename}
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove ${a.filename}`}
                        onClick={() => removeAttachment(a.path)}
                        className="grid h-5 w-5 place-items-center rounded-full bg-white/10 text-white hover:bg-[#8A2BE2] transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button
              type="submit"
              data-testid="contact-submit-button"
              disabled={loading}
              className="w-full md:w-auto rounded-full bg-[#8A2BE2] hover:bg-[#9D4EDD] text-white font-medium px-8 py-6 text-base transition-colors duration-300"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  Send Message <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
